import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Eye, Trash2, RefreshCw, Plus } from "lucide-react";
import { matrixApi, matrixError } from "@/lib/matrixApi";

const when = (iso) => (iso ? new Date(iso).toLocaleString("es-MX", {
  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "nunca");

/** Webs vigiladas: cada día se revisan solas y avisan por Telegram si cambian. */
export const MatrixWatch = ({ onClose, onCount, defaultUrl = "" }) => {
  const [list, setList] = useState([]);
  const [url, setUrl] = useState(defaultUrl);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(null);

  const load = async () => {
    try {
      const { data } = await matrixApi.get("/watch");
      setList(data.watches);
      onCount?.(data.watches.filter((w) => w.enabled).length);
    } catch (e) { toast.error(matrixError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const add = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await matrixApi.post("/watch", { url, label: "", click: false, pages: 1 });
      setUrl("");
      load();
      toast.success("Web vigilada: te aviso por Telegram si cambia");
    } catch (e) { toast.error(matrixError(e)); }
    finally { setBusy(false); }
  };

  const check = async (id) => {
    setChecking(id);
    try {
      const { data } = await matrixApi.post(`/watch/${id}/check`);
      if (!data.ok) toast.error(data.error);
      else if (data.first) toast.success("Primera revisión guardada: ya tengo con qué comparar");
      else if (Object.keys(data.changes || {}).length) toast.warning("¡Cambió! Mira el detalle");
      else toast.success("Sin cambios");
      load();
    } catch (e) { toast.error(matrixError(e)); }
    finally { setChecking(null); }
  };

  const toggle = async (w) => {
    try {
      await matrixApi.patch(`/watch/${w.watch_id}?enabled=${!w.enabled}`);
      load();
    } catch (e) { toast.error(matrixError(e)); }
  };
  const del = async (id) => {
    try {
      await matrixApi.delete(`/watch/${id}`);
      load();
    } catch (e) { toast.error(matrixError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-5"
      data-testid="matrix-watch-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="glass-card forge-card p-5 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="font-heading text-white tracking-wider flex items-center gap-2">
            <Eye className="w-4 h-4 text-red-400" /> WEBS VIGILADAS
          </span>
          <button onClick={onClose} data-testid="matrix-watch-close"
            className="text-white/50 hover:text-white">✕</button>
        </div>

        <p className="text-[11px] text-white/45 mb-2">
          Cada día reviso solas estas webs y te aviso por Telegram si cambian sus APIs,
          sus dominios, sus botones o su forma de cobrar. Las revisiones son gratis.
        </p>
        <div className="flex gap-2 mb-4">
          <input data-testid="matrix-watch-url" value={url} onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="tienda.com"
            className="flex-1 glass-card px-3 py-2 bg-black/50 text-white text-xs outline-none" />
          <button data-testid="matrix-watch-add" onClick={add} disabled={busy}
            className="matrix-btn px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Vigilar
          </button>
        </div>

        <div className="glass-card divide-y divide-white/5">
          {list.length === 0 && (
            <p className="text-xs text-white/40 p-4">Todavía no vigilas ninguna web.</p>
          )}
          {list.map((w) => (
            <div key={w.watch_id} className="px-3 py-2.5 flex items-center gap-3 text-xs"
              data-testid={`matrix-watch-${w.watch_id}`}>
              <button onClick={() => toggle(w)} data-testid={`matrix-watch-toggle-${w.watch_id}`}
                className={`w-9 h-5 rounded-full relative shrink-0 transition-colors ${
                  w.enabled ? "bg-red-600/80" : "bg-white/15"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                  w.enabled ? "left-4" : "left-0.5"}`} />
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-white/85 truncate">{w.url}</div>
                <div className="text-[10px] text-white/35">
                  Revisada: {when(w.last_check)}
                  {w.last_change && <span className="text-amber-300"> · cambió {when(w.last_change)}</span>}
                  {w.changes > 0 && <span> · {w.changes} cambios</span>}
                  {w.last_error && <span className="text-rose-400"> · {w.last_error}</span>}
                </div>
              </div>
              <button onClick={() => check(w.watch_id)} data-testid={`matrix-watch-check-${w.watch_id}`}
                className="text-white/50 hover:text-red-300 shrink-0" title="Revisar ahora">
                {checking === w.watch_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => del(w.watch_id)} data-testid={`matrix-watch-del-${w.watch_id}`}
                className="text-white/50 hover:text-red-400 shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
